/**
 * FilterPanelContent - Reusable filter controls panel
 *
 * Renders a grid of filter controls (MultiSelect dropdowns) from a configuration.
 * Used inside the ForecastControlsFAB popover and can be reused in other contexts.
 *
 * This component extracts the filter rendering logic from ForecastFilters to enable
 * DRY usage across different container components (popover, sheet, collapsible, etc.)
 */

import React from 'react';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { cn } from '@/lib/utils';

export interface FilterGroup {
  /** Display label for the filter */
  label: string;
  /** Available options */
  options: MultiSelectOption[];
  /** Currently selected values */
  value: MultiSelectOption[];
  /** Handler for selection changes */
  onChange: (selected: MultiSelectOption[]) => void;
  /** Placeholder text when nothing selected */
  placeholder?: string;
  /** Placeholder for search input */
  searchPlaceholder?: string;
  /** Key for grouping options */
  groupKey?: string;
  /** Whether the filter has an error state */
  error?: boolean;
  /** Whether to auto-sort options */
  shouldAutoSort?: boolean;
  /** Custom renderer for selected display */
  renderSelectedDisplay?: (
    value: MultiSelectOption[],
    totalOptionsCount: number,
  ) => React.ReactNode | null;
}

export interface FilterPanelContentProps {
  /** Filter group configurations */
  filterGroups: FilterGroup[];
  /** Layout direction */
  layout?: 'horizontal' | 'vertical' | 'grid';
  /** Additional class names for the container */
  className?: string;
}

export function FilterPanelContent({
  filterGroups,
  layout = 'grid',
  className,
}: FilterPanelContentProps) {
  const layoutClasses = {
    horizontal: 'flex flex-wrap gap-4',
    vertical: 'flex flex-col gap-4',
    grid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
  };

  return (
    <div className={cn(layoutClasses[layout], className)}>
      {filterGroups.map((filter, index) => {
        const {
          label,
          error,
          shouldAutoSort,
          renderSelectedDisplay,
          ...multiSelectProps
        } = filter;

        return (
          <div key={index} className="flex flex-col min-w-[180px]">
            {label && (
              <label
                className={cn(
                  'text-sm font-medium mb-1.5',
                  error && 'text-destructive',
                )}
              >
                {label}
              </label>
            )}
            <MultiSelect
              {...multiSelectProps}
              size="sm"
              maxChips={2}
              liveUpdate={false}
              hideChipsWhenAllSelected={true}
              error={error}
              shouldAutoSort={shouldAutoSort}
              renderSelectedDisplay={renderSelectedDisplay}
            />
          </div>
        );
      })}
    </div>
  );
}

export type { MultiSelectOption };
